(function () {
  /** @type {Map<string, { bar: HTMLElement, io: IntersectionObserver, target: Element }>} */
  const instances = new Map();

  let footerVisible = false;
  let footerObserver = null;

  function getProductInfo(sectionId) {
    return document.querySelector(`product-info[data-section="${sectionId}"]`);
  }

  function getFooter() {
    return (
      document.querySelector('.shopify-section.footer-r3nu') ||
      document.querySelector('#shopify-section-group-footer-group') ||
      document.querySelector('footer')
    );
  }

  function isFooterIntersecting(footer) {
    const rect = footer.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < vh && rect.bottom > 0;
  }

  function observeFooter() {
    const footer = getFooter();
    if (!footer) return;

    if (footerObserver) {
      footerObserver.disconnect();
      footerObserver = null;
    }

    footerVisible = isFooterIntersecting(footer);

    footerObserver = new IntersectionObserver(
      (entries) => {
        footerVisible = entries[0]?.isIntersecting ?? false;
        refreshAllVisibility();
      },
      { root: null, threshold: 0 }
    );

    footerObserver.observe(footer);
  }

  function refreshAllVisibility() {
    instances.forEach(({ bar, target }) => updateVisibility(bar, target));
  }

  function getBuyButtonsTarget(productInfo, sectionId) {
    return (
      productInfo.querySelector('.r3nu-product-block-buy .product-form__buttons--r3nu') ||
      productInfo.querySelector(`#ProductSubmitButton-${sectionId}`)?.closest('.product-form__buttons') ||
      productInfo.querySelector('[data-r3nu-sticky-cta-sentinel]')
    );
  }

  /**
   * Show sticky bar only when the main buy block has scrolled up past the viewport.
   * (Not when it is below the fold — IO alone would treat that as non-intersecting too.)
   */
  function shouldShowSticky(entry) {
    if (!entry) return false;
    if (entry.isIntersecting) return false;
    return entry.boundingClientRect.bottom < 0;
  }

  function syncPrice(bar, html, sectionId) {
    const dest = bar.querySelector(`#sticky-price-${sectionId}`);
    const source = html
      ? html.getElementById(`price-${sectionId}`)
      : getProductInfo(sectionId)?.querySelector(`#price-${sectionId}`);
    if (!dest || !source) return;

    const price = source.querySelector('.price');
    if (!price) return;

    const clone = price.cloneNode(true);
    clone.classList.add('price--sticky-cta');
    dest.replaceChildren(clone);
  }

  function syncButtonState(bar, sectionId) {
    const productInfo = getProductInfo(sectionId);
    if (!productInfo) return;

    const mainBuyNow = productInfo.querySelector(`#ProductSubmitButton-${sectionId}`);
    const stickyBuyBtns = bar.querySelectorAll('[data-r3nu-sticky-buy-now]');

    stickyBuyBtns.forEach((btn) => {
      if (btn.classList.contains('loading')) return;

      const mainDisabled =
        mainBuyNow?.disabled ||
        mainBuyNow?.hasAttribute('disabled') ||
        mainBuyNow?.getAttribute('aria-disabled') === 'true';
      btn.disabled = !!mainDisabled;
      btn.toggleAttribute('aria-disabled', !!mainDisabled);
    });
  }

  function updateBodyStickyClass() {
    const anyVisible = document.querySelector('.r3nu-product-sticky-cta.r3nu-product-sticky-cta--visible');
    document.documentElement.classList.toggle('r3nu-has-sticky-product-cta', !!anyVisible);
  }

  function setVisible(bar, visible) {
    bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    bar.toggleAttribute('inert', !visible);
    bar.classList.toggle('r3nu-product-sticky-cta--visible', visible);
    updateBodyStickyClass();
  }

  function entryFromRect(rect) {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const isIntersecting = rect.bottom > 0 && rect.top < vh;
    return { isIntersecting, boundingClientRect: rect };
  }

  function updateVisibility(bar, target) {
    if (!target) return;
    const showFromBuyButtons = shouldShowSticky(entryFromRect(target.getBoundingClientRect()));
    setVisible(bar, showFromBuyButtons && !footerVisible);
  }

  function teardown(sectionId) {
    const instance = instances.get(sectionId);
    if (!instance) return;
    instance.io.disconnect();
    instances.delete(sectionId);

  }

  function initBar(bar, force) {
    const sectionId = bar.dataset.sectionId;
    if (!sectionId) return;

    if (instances.has(sectionId) && !force) return;

    teardown(sectionId);

    const productInfo = getProductInfo(sectionId);
    const target = productInfo && getBuyButtonsTarget(productInfo, sectionId);
    if (!productInfo || !target) return;

    const io = new IntersectionObserver(
      () => {
        updateVisibility(bar, target);
      },
      { root: null, threshold: [0, 1] }
    );

    io.observe(target);
    instances.set(sectionId, { bar, io, target });

    // Mirror the main PDP buy-now label (theme setting) onto the sticky one.
    const mainBuyNowLabel = productInfo.querySelector(`#ProductBuyNow-${sectionId} .r3nu-primary-cta__label`);
    const stickyBuyNowLabel = bar.querySelector('[data-r3nu-buy-now] .r3nu-primary-cta__label');
    if (mainBuyNowLabel && stickyBuyNowLabel) stickyBuyNowLabel.textContent = mainBuyNowLabel.textContent;

    syncButtonState(bar, sectionId);
    syncPrice(bar, null, sectionId);
    updateVisibility(bar, target);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      if (!bar.dataset.r3nuVariantSubscribed) {
        bar.dataset.r3nuVariantSubscribed = 'true';
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event.data?.sectionId) !== sectionId) return;
          syncPrice(bar, event.data.html, sectionId);
          syncButtonState(bar, sectionId);
          const inst = instances.get(sectionId);
          if (inst?.target) updateVisibility(bar, inst.target);
        });
      }
    }
  }

  function initAll() {
    observeFooter();
    document.querySelectorAll('[data-r3nu-sticky-cta]').forEach((bar) => initBar(bar, true));
    if (typeof window.r3nuBindBuyNow === 'function') window.r3nuBindBuyNow();
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);

  document.addEventListener(
    'product-info:loaded',
    (event) => {
      const productInfo = event.target;
      if (!productInfo?.dataset?.section) return;
      const bar = document.querySelector(
        `[data-r3nu-sticky-cta][data-section-id="${productInfo.dataset.section}"]`
      );
      if (bar) initBar(bar, true);
    },
    true
  );

  let scrollRaf = 0;
  function onScrollOrResize() {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      instances.forEach(({ bar, target }) => updateVisibility(bar, target));
    });
  }

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });
})();
